import { useEffect } from 'react'
import { Skeleton } from '@/components/ui/skeleton'
import { dismissBootSplash } from '@/lib/boot-splash'
import { SURFACE_CARD } from '@/lib/surfaces'

/**
 * Neutral page-shaped skeleton for the content area while the organization,
 * its feature flags or its subscription features load. Route guards render
 * this instead of a bare centred spinner so a page in flight already has the
 * shape of the page it becomes (design audit S12). The sidebar and header
 * from ProtectedLayout are already visible at this point.
 */
export function PageBootSkeleton() {
  // Slow-network escape hatch for the desktop boot splash: the splash
  // normally stays up until the destination page renders (so this skeleton
  // is never seen on boot), but if org data is taking this long, reveal the
  // skeleton rather than holding the splash indefinitely.
  useEffect(() => {
    const timeoutId = setTimeout(
      () => dismissBootSplash({ waitForGlass: true }),
      2500,
    )
    return () => clearTimeout(timeoutId)
  }, [])

  return (
    <div className="flex h-full flex-col gap-4 p-6" aria-busy="true">
      <div className="flex items-center justify-between">
        <Skeleton className="h-7 w-40" />
        <div className="flex gap-2">
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-8 w-8" />
        </div>
      </div>
      {/* The content is a white sheet, as the page it becomes will be. */}
      <div
        className={`${SURFACE_CARD} flex min-h-0 flex-1 flex-col gap-3 overflow-hidden p-4`}
      >
        <div className="flex gap-2">
          <Skeleton className="h-8 w-56" />
          <Skeleton className="h-8 w-24" />
        </div>
        <div className="flex-1 space-y-2 overflow-hidden">
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton key={index} className="h-10 w-full" />
          ))}
        </div>
      </div>
    </div>
  )
}
